import { Controller } from "@hotwired/stimulus"

const MILES_TO_METERS = 1609.34

// Connects to data-controller="job-announcement-form"
export default class extends Controller {
  static targets = ["usersTable", "radius", "contracts", "wrapper", "submitButton", "search", "userIds", "selectedText", "loader", "nextPageLoader", "noUsersFound"]
  static values = {
    updateUsersTableUrl: String
  }

  connect() {
    if (this.hasSearchTarget) {
      this.searchTarget.addEventListener("input", this.updateUsersTable.bind(this))
    }
  }

  disconnect() {
    this.teardownRadiusOverlay()
  }

  mapReady(event) {
    this.teardownRadiusOverlay()
    this.mapContext = event.detail
    this.setupRadiusOverlay()
    this.updateRadiusOverlay()
  }

  mapDisconnected() {
    this.teardownRadiusOverlay()
    this.mapContext = null
  }

  radiusInputChanged() {
    this.updateRadiusOverlay()
    this.updateUsersTable()
  }

  updateUsersTable() {
    this.showLoadingSpinner()
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.performUpdateUsersTable()
    }, 500)
  }

  performUpdateUsersTable() {
    const radius = this.radiusTarget.value
    const contracts = Array.from(this.contractsTarget.selectedOptions).map((option) => option.value)

    let url = `${this.updateUsersTableUrlValue}?radius=${radius}&search=${encodeURIComponent(this.search)}`

    if (contracts && contracts.length > 0) {
      contracts.forEach((contract) => {
        url += `&contracts[]=${encodeURIComponent(contract)}`
      })
    }

    this.usersTableTarget.src = url
  }

  usersTableFrameLoaded() {
    const table = this.usersTableTarget.querySelector("table")
    if (table) {
      table.classList.remove("hidden")
    }

    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add("hidden")
    }

    this.updateSelectedUsers()
  }

  nextPageTargetConnected() {
    const event = new CustomEvent("data-added")
    this.wrapperTarget.dispatchEvent(event)
  }

  updateSubmitConfirmationText() {
    const selectedCount = this.usersTableTarget.querySelectorAll('tbody input[type="checkbox"]:checked').length
    if (selectedCount > 0) {
      this.submitButtonTarget.dataset.confirm = `Are you sure you would like to send this announcement to ${selectedCount} users?`
    } else {
      this.submitButtonTarget.dataset.confirm = ""
    }
  }

  updateSelectedUsers() {
    const currentStoredUsers = this.userIds ? this.userIds.split(",") : []
    this.usersTableTarget.querySelectorAll('tbody input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = currentStoredUsers.includes(checkbox.value)
    })

    if (this.userIds != "") {
      const selectedCount = this.userIds.split(",").length
      this.selectedTextTarget.textContent = `(${selectedCount} selected)`
    }
  }

  storeSelectedUsers(e) {
    const visibleUserIds = Array.from(
      this.usersTableTarget.querySelectorAll('tbody input[type="checkbox"]')
    ).map((checkbox) => checkbox.value)

    let currentStoredUsers = (this.userIds || "").split(",").filter(Boolean)

    if (e.detail?.bulk) {
      if (e.detail.select_all_checked) {
        visibleUserIds.forEach((userId) => {
          if (!currentStoredUsers.includes(userId)) {
            currentStoredUsers.push(userId)
          }
        })
      } else {
        currentStoredUsers = currentStoredUsers.filter((userId) => !visibleUserIds.includes(userId))
      }
    } else if (e.detail?.original_target) {
      const { original_target: target } = e.detail

      if (target.checked) {
        if (!currentStoredUsers.includes(target.value)) {
          currentStoredUsers.push(target.value)
        }
      } else {
        currentStoredUsers = currentStoredUsers.filter((userId) => userId !== target.value)
      }
    }

    this.userIdsTarget.value = currentStoredUsers.join(",")
  }

  showLoadingSpinner() {
    const table = this.usersTableTarget.querySelector("table")
    if (table) {
      table.classList.add("hidden")
    }

    if (!this.hasNextPageLoaderTarget && this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hidden")
    }

    if (this.hasNoUsersFoundTarget) {
      this.noUsersFoundTarget.classList.add("hidden")
    }
  }

  setupRadiusOverlay() {
    if (!this.mapContext) return

    const { map, lat, lng } = this.mapContext
    const center = [lng, lat]
    const radiusSourceId = `${this.mapContext.mapId}-radius`
    const radiusFillLayerId = `${radiusSourceId}-fill`
    const radiusStrokeLayerId = `${radiusSourceId}-stroke`

    this.updateRadiusCircle = (radiusMiles) => {
      const circleGeoJSON = this.createCircleGeoJSON(center, radiusMiles)

      if (map.getLayer(radiusFillLayerId)) map.removeLayer(radiusFillLayerId)
      if (map.getLayer(radiusStrokeLayerId)) map.removeLayer(radiusStrokeLayerId)
      if (map.getSource(radiusSourceId)) map.removeSource(radiusSourceId)

      map.addSource(radiusSourceId, { type: "geojson", data: circleGeoJSON })
      map.addLayer({
        id: radiusFillLayerId,
        type: "fill",
        source: radiusSourceId,
        paint: { "fill-color": "#000000", "fill-opacity": 0.35 }
      })
      map.addLayer({
        id: radiusStrokeLayerId,
        type: "line",
        source: radiusSourceId,
        paint: { "line-color": "#000000", "line-width": 2, "line-opacity": 0.8 }
      })
    }

    this.radiusSourceId = radiusSourceId
    this.radiusFillLayerId = radiusFillLayerId
    this.radiusStrokeLayerId = radiusStrokeLayerId
  }

  updateRadiusOverlay() {
    const miles = this.currentRadiusMiles

    if (this.hiddenRadiusInput) {
      this.hiddenRadiusInput.value = miles
    }
    if (this.radiusValueElement) {
      this.radiusValueElement.textContent = miles
    }

    if (!this.mapContext || !this.updateRadiusCircle) return

    this.updateRadiusCircle(miles)
  }

  teardownRadiusOverlay() {
    if (this.mapContext && this.radiusSourceId) {
      const { map } = this.mapContext
      if (map.getLayer(this.radiusFillLayerId)) map.removeLayer(this.radiusFillLayerId)
      if (map.getLayer(this.radiusStrokeLayerId)) map.removeLayer(this.radiusStrokeLayerId)
      if (map.getSource(this.radiusSourceId)) map.removeSource(this.radiusSourceId)
    }

    this.updateRadiusCircle = null
    this.radiusSourceId = null
    this.radiusFillLayerId = null
    this.radiusStrokeLayerId = null
  }

  createCircleGeoJSON(center, radiusMiles) {
    const radiusMeters = radiusMiles * MILES_TO_METERS
    const earthRadius = 6378137
    const latRad = (center[1] * Math.PI) / 180
    const lngRad = (center[0] * Math.PI) / 180
    const angularDistance = radiusMeters / earthRadius

    const coordinates = []
    for (let i = 0; i <= 64; i++) {
      const angle = ((i * 360) / 64) * (Math.PI / 180)
      const lat = Math.asin(
        Math.sin(latRad) * Math.cos(angularDistance) +
          Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(angle)
      )
      const lng =
        lngRad +
        Math.atan2(
          Math.sin(angle) * Math.sin(angularDistance) * Math.cos(latRad),
          Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(lat)
        )
      coordinates.push([(lng * 180) / Math.PI, (lat * 180) / Math.PI])
    }
    coordinates.push(coordinates[0])

    return {
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [coordinates] }
    }
  }

  get currentRadiusMiles() {
    if (this.hasRadiusTarget && this.radiusTarget.value !== "") {
      return parseInt(this.radiusTarget.value, 10)
    }
    return 10
  }

  get hiddenRadiusInput() {
    return this.element.querySelector('input[name="job_announcement[radius]"].hidden')
  }

  get radiusValueElement() {
    const sliderId = this.hasRadiusTarget ? this.radiusTarget.id : null
    return sliderId ? document.getElementById(`${sliderId}-value`) : null
  }

  get search() {
    if (this.hasSearchTarget) {
      return this.searchTarget.value
    }
    return ""
  }

  get userIds() {
    return this.userIdsTarget.value
  }
}
